import React, { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Select } from '@panwds/react-ui';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { FilterValueButton } from './FilterValueButton.esm.js';

const emptyItems = [];
function toArray(itemOrItems) {
    return Array.isArray(itemOrItems)
        ? itemOrItems
        : itemOrItems
            ? [itemOrItems]
            : [];
}
/**
 * Provides a Select that will set the filter state for the name given
 * to this component.
 *
 * **Note**: If you have a lot of items in your list (10k+) it may start to slow down.
 * Use StaticSelectFilter or paginate depending on your use case.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectFilter } from "@panwds/react-filters";
 * ```
 */
function SelectFilter({ autoFocus, name, items = emptyItems, label, onClose, disabled = false, id = "select-filter", ...rest }) {
    const intl = useIntl();
    const { filters, setFilters } = useFilters();
    // remember full items even if `items` changes to omit them
    const [selectedItemsRecord] = useState(new Map());
    const handleOnChange = useCallback((changes) => {
        selectedItemsRecord.set(changes.selectedItem.value, changes.selectedItem.children || undefined);
        if (rest.enableMultiSelect) {
            const filteredItemValues = items.map((item) => item.value);
            const selectedValues = filters[name] || [];
            // When this component is controlled, we don't want to lose selected
            // filter values that are not present in items that may have been filtered.
            const selectedItemsNotInFilteredList = Array.isArray(selectedValues)
                ? selectedValues.filter((value) => !filteredItemValues.includes(value))
                : [];
            setFilters({
                [name]: [
                    ...changes.selectedItems.map((item) => item.value),
                    ...selectedItemsNotInFilteredList,
                ],
            });
        }
        else {
            setFilters({
                [name]: `${changes.selectedItem.value}`,
            });
        }
    }, [items, filters, setFilters, rest.enableMultiSelect]);
    const selectedItem = useMemo(() => {
        if (rest.enableMultiSelect) {
            return items.filter((item) => {
                const values = filters[name];
                return Array.isArray(values) && values.includes(item.value);
            });
        }
        else {
            return items.find((item) => {
                return item.value === filters[name];
            });
        }
    }, [items, filters[name], rest.enableMultiSelect]);
    const selectedValues = toArray(filters[name]);
    const selectedItemLabels = selectedValues.map((val) => { var _a; return (_a = selectedItemsRecord.get(val)) !== null && _a !== void 0 ? _a : val; });
    const select = (React.createElement("div", { id: id },
        React.createElement(Select, { ...rest, disabled: disabled, items: items, button: React.createElement(FilterValueButton, { autoFocus: autoFocus, enableSelectAll: rest.enableSelectAll, selectedItemLabels: selectedItemLabels, items: items, label: intl.formatMessage({ id: "I4bSrq", defaultMessage: [{ type: 1, value: "label" }] }, {
                    label: label,
                }), name: name, disabled: disabled, onClose: onClose, required: rest.required, isFullWidth: rest.isFullWidth }), onChange: handleOnChange, selectedItem: selectedItem })));
    return select;
}

export { SelectFilter };
//# sourceMappingURL=SelectFilter.esm.js.map
